// packages/shared/src/constants.ts
//
// Limits, TTLs, quotas and image bounds. Values that appear in more than one
// app live here so the widget, the API and the worker cannot disagree about
// them — a quality gate that passes on the client and fails on the server is
// the worst version of this bug.
//
// Anything a tenant can change is NOT here. This is platform policy.

/** Capture and upload bounds. */
export const IMAGE = {
  /** Minimum capture resolution. Below this the quality gate cannot judge sharpness. */
  MIN_CAPTURE_WIDTH: 1280,
  MIN_CAPTURE_HEIGHT: 720,
  /** Longest edge after client downscale. ~150-250KB at the quality below. */
  MAX_EDGE_PX: 1280,
  JPEG_QUALITY: 0.85,
  /** Margin added around the detected face box before cropping, as a fraction. */
  FACE_CROP_MARGIN: 0.2,
  MAX_UPLOAD_BYTES: 8 * 1024 * 1024,
  ACCEPTED_MIME: ['image/jpeg', 'image/png', 'image/webp'] as const,
} as const;

/**
 * Client-side quality gate thresholds.
 *
 * Every one of these produces a specific instruction rather than an error —
 * "move toward a window", not "capture failed". This gate does more for
 * perceived accuracy than any prompt tuning, because an underexposed photo
 * produces numbers nobody should trust and the clinic blames the AI.
 */
export const QUALITY_GATE = {
  /** Exactly one face, occupying this fraction of frame height. */
  FACE_HEIGHT_MIN: 0.25,
  FACE_HEIGHT_MAX: 0.6,
  MAX_FACES: 1,
  /** Pose, in degrees, from FaceMesh landmarks. */
  MAX_YAW_DEG: 12,
  MAX_PITCH_DEG: 12,
  MAX_ROLL_DEG: 10,
  /** Mean luma of the face region. */
  BRIGHTNESS_MIN: 90,
  BRIGHTNESS_MAX: 190,
  MAX_CLIPPED_FRACTION: 0.15,
  /** Left/right cheek luma ratio. Catches harsh side lighting. */
  EVENNESS_MIN: 0.7,
  EVENNESS_MAX: 1.4,
  /** Laplacian variance floor — blur and motion. */
  SHARPNESS_MIN: 55,
  /** Consecutive passing frames before the capture button enables. */
  STABLE_FRAMES: 8,
} as const;

/** Scan session and QR handoff. */
export const SESSION = {
  TTL_SECONDS: 600,
  HANDOFF_TOKEN_BYTES: 16, // 128 bits
  HANDOFF_SINGLE_USE: true,
  /** SSE keepalive, below the 60s most proxies idle out at. */
  SSE_HEARTBEAT_SECONDS: 25,
} as const;

/** Analysis provider. */
export const ANALYSIS = {
  /** Parallel passes, median severity per metric. Lowering this raises variance. */
  PASSES: 3,
  TEMPERATURE: 0,
  TOP_K: 1,
  /** Dedupe cache on the SHA-256 of the normalised buffer. Matches photo retention. */
  CACHE_TTL_SECONDS: 24 * 3600,
  // Thinking models (gemini-3.x flash) take 10–40s for a two-image read; at 30s
  // a slow minute timed out every pass and failed the scan.
  TIMEOUT_MS: 60_000,
  MAX_RETRIES: 2,
  /** Below this mean confidence, escalate to the pro model or ask for a retake. */
  LOW_CONFIDENCE: 0.55,
  /** Spread across passes above which the report shows a range, not a point. */
  MAX_SEVERITY_SPREAD: 2,
} as const;

/**
 * Retention. Photos are ephemeral by default and this is a compliance value,
 * not a tuning value — see docs/12-security-compliance.md §4.
 */
export const RETENTION = {
  PHOTO_HOURS_DEFAULT: 0,
  /** R2 lifecycle backstop. Deletes anything left behind regardless. */
  PHOTO_LIFECYCLE_HOURS: 24,
  /** Grace period between tenant cancellation and hard delete. Stated in the contract. */
  TENANT_OFFBOARD_DAYS: 30,
} as const;

/** Trial and quota policy. Mirrors the env defaults; see docs/13-operations.md §3. */
export const TRIAL = {
  TEST_MODE_FREE_SCANS: 10,
  TRIAL_DAYS: 30,
  TRIAL_SCAN_LIMIT: 50,
} as const;

/** Rate limits, per window, backed by Redis. */
export const RATE_LIMIT = {
  /**
   * Anonymous traffic only — the widget, sign-in, password reset. Never applied
   * to signed-in staff: a clinic's whole team shares one office IP, and a single
   * dashboard page makes several requests, so a per-IP budget throttled real use.
   */
  PER_IP: { points: 30, windowSeconds: 60 },
  /** Signed-in people, each their own budget. Far beyond what a person clicks. */
  PER_USER: { points: 600, windowSeconds: 60 },
  PER_SESSION: { points: 10, windowSeconds: 60 },
  /**
   * The desktop watching a phone scan: its event stream and, where a proxy
   * buffers that stream, a status poll every two seconds. Reads only, and kept
   * apart from the budgets above — sharing them meant the desktop's polling used
   * up the session's budget and the phone's upload was refused.
   */
  SESSION_WATCH: { points: 45, windowSeconds: 60 },
  PER_TENANT: { points: 300, windowSeconds: 60 },
  /** Analysis is the expensive one and gets its own budget. */
  ANALYZE_PER_IP: { points: 5, windowSeconds: 300 },
} as const;

/** Cursor pagination. Offset breaks on the write-heavy scan tables. */
export const PAGINATION = {
  DEFAULT_LIMIT: 25,
  MAX_LIMIT: 100,
} as const;

/** Minimum age to scan, by region. Simulation gates separately and higher. */
export const AGE_GATE = {
  DEFAULT_MIN_AGE: 16,
  /** Jurisdictions where 18 is the safer floor for biometric consent. */
  STRICT_MIN_AGE: 18,
  STRICT_COUNTRIES: ['US'] as const,
  SIMULATION_MIN_AGE: 18,
} as const;

/** Outbound webhook delivery. */
export const WEBHOOK = {
  TIMEOUT_MS: 10_000,
  MAX_ATTEMPTS: 6,
  /** Exponential backoff in seconds: ~1m, 5m, 30m, 2h, 6h. */
  BACKOFF_SECONDS: [60, 300, 1800, 7200, 21600],
  SIGNATURE_HEADER: 'X-SkinVizo-Signature',
  /** Reject replays older than this. */
  TOLERANCE_SECONDS: 300,
} as const;

/** Home-care routine slots, in the order they are applied. */
export const ROUTINE_SLOTS = [
  'am_cleanser',
  'am_serum',
  'am_moisturiser',
  'am_spf',
  'pm_cleanser',
  'pm_treatment',
  'pm_moisturiser',
] as const;

export type RoutineSlot = (typeof ROUTINE_SLOTS)[number];

export const ROUTINE_SLOT_LABELS: Record<RoutineSlot, string> = {
  am_cleanser: 'AM · Cleanse',
  am_serum: 'AM · Treat',
  am_moisturiser: 'AM · Moisturise',
  am_spf: 'AM · Protect',
  pm_cleanser: 'PM · Cleanse',
  pm_treatment: 'PM · Treat',
  pm_moisturiser: 'PM · Moisturise',
};
